import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import {
  cloneForceGraphData,
  createMultiSeedRadialForce,
  createRadialHopForce,
  EDGE_LABEL_ZOOM,
  ENTITY_TYPE_COLORS,
  FOCUS_RING_COLOR,
  formatLinkLabel,
  linkDistanceForWeight,
  linkStrokeStyle,
  seedMultiSeedRingPositions,
} from '../services/graphApi';

const TYPE_CLASS = {
  ORG: 'memetic-node-org',
  PERSON: 'memetic-node-person',
  GPE: 'memetic-node-gpe',
  EVENT: 'memetic-node-event',
};

const RING_RADIUS_2D = 90;
const SEED_RING = 48;
const DIM_NODE_ALPHA = 0.18;
const DIM_LINK_ALPHA = 0.06;
/** Extra graph-space padding for node click targets (Starburst circles). */
const NODE_HIT_PAD = 4;

function nodeRadius(node, { isSeed = false, globalScale = 1 } = {}) {
  let base = node.hop === 0 ? 8 : 5;
  const scale = Math.sqrt(Math.max(node.articleCount || 1, 1));
  base = Math.min(base + scale, 14);
  if (isSeed && globalScale < 0.6) {
    base = Math.max(base, 12);
  }
  return base;
}

function drawSeedGlow(ctx, x, y, radius, globalScale, { multiSeed = false } = {}) {
  ctx.beginPath();
  ctx.arc(x, y, radius + 4 / globalScale, 0, 2 * Math.PI, false);
  ctx.strokeStyle = multiSeed ? 'rgba(125, 211, 252, 0.55)' : 'rgba(80, 255, 106, 0.45)';
  ctx.lineWidth = 1.5 / globalScale;
  ctx.stroke();
}

function drawNodeLabel(ctx, node, radius, globalScale, { isSeed = false, dimmed = false } = {}) {
  if (dimmed) {
    return;
  }
  const showLabel = isSeed || globalScale > 0.75;
  if (!showLabel) {
    return;
  }
  const label = node.name || '';
  const fontSize = isSeed ? Math.max(12 / globalScale, 3.5) : Math.max(10 / globalScale, 3);
  ctx.font = `${isSeed ? 600 : 400} ${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#e8edf2';
  const maxLen = isSeed ? 32 : 28;
  const sliceLen = maxLen - 2;
  ctx.fillText(
    label.length > maxLen ? `${label.slice(0, sliceLen)}…` : label,
    node.x,
    node.y + radius + 2,
  );
}

function endpointId(endpoint) {
  if (endpoint == null) {
    return null;
  }
  return String(typeof endpoint === 'object' ? endpoint.id : endpoint);
}

function configureForces(graphRef, { multiSeed = false } = {}) {
  const fg = graphRef.current;
  if (!fg) {
    return;
  }

  const linkForce = fg.d3Force('link');
  if (linkForce) {
    linkForce.distance((link) => linkDistanceForWeight(link.weight));
  }

  const chargeForce = fg.d3Force('charge');
  if (chargeForce) {
    chargeForce.strength(-90);
    if (typeof chargeForce.distanceMax === 'function') {
      chargeForce.distanceMax(RING_RADIUS_2D * 4);
    }
  }

  if (multiSeed) {
    fg.d3Force('radial', createMultiSeedRadialForce({
      ringRadius: RING_RADIUS_2D,
      seedRing: SEED_RING,
      strength: 0.35,
    }));
  } else {
    fg.d3Force('radial', createRadialHopForce({ ringRadius: RING_RADIUS_2D, strength: 0.35 }));
  }
  fg.d3ReheatSimulation?.();
}

const MemeticGraphCanvas = forwardRef(({
  graphData,
  centerId,
  seedIds = null,
  onNodeClick,
  reducedMotion = false,
  layoutKey = 0,
  focusNodeIds = null,
}, ref) => {
  const graphRef = useRef(null);
  const hostRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [highlightNodes, setHighlightNodes] = useState(null);
  const [hoveredLink, setHoveredLink] = useState(null);

  /** External focus (e.g. Coverage timeline hover) uses the same dim treatment as canvas hover. */
  const focusHighlight = useMemo(() => {
    const keys = (focusNodeIds || []).map((id) => String(id)).filter(Boolean);
    return keys.length > 0 ? new Set(keys) : null;
  }, [focusNodeIds]);

  const data = useMemo(() => cloneForceGraphData(graphData), [graphData]);

  const seedKeySet = useMemo(() => {
    const ids = seedIds && seedIds.length > 0
      ? seedIds
      : (centerId != null ? [centerId] : []);
    return new Set(ids.filter(Boolean).map((id) => String(id)));
  }, [seedIds, centerId]);

  const multiSeed = seedKeySet.size > 1;

  const activeHighlight = highlightNodes ?? focusHighlight;

  const adjacency = useMemo(() => {
    const map = new Map();
    data.nodes.forEach((node) => map.set(String(node.id), new Set()));
    data.links.forEach((link) => {
      const s = endpointId(link.source);
      const t = endpointId(link.target);
      if (map.has(s) && map.has(t)) {
        map.get(s).add(t);
        map.get(t).add(s);
      }
    });
    return map;
  }, [data]);

  const buildNeighborhood = useCallback((...ids) => {
    const set = new Set();
    ids.filter(Boolean).forEach((id) => {
      const key = String(id);
      set.add(key);
      (adjacency.get(key) || []).forEach((neighbor) => set.add(neighbor));
    });
    return set.size > 0 ? set : null;
  }, [adjacency]);

  const handleNodeHover = useCallback((node) => {
    // Seed hover is a no-op; only hop >= 1 neighbors trigger neighborhood focus.
    if (!node || seedKeySet.has(String(node.id)) || (node.hop ?? 0) < 1) {
      setHighlightNodes(null);
      return;
    }
    setHighlightNodes(buildNeighborhood(node.id));
  }, [buildNeighborhood, seedKeySet]);

  const handleLinkHover = useCallback((link) => {
    if (!link) {
      setHighlightNodes(null);
      setHoveredLink(null);
      return;
    }
    setHoveredLink(link);
    setHighlightNodes(buildNeighborhood(endpointId(link.source), endpointId(link.target)));
  }, [buildNeighborhood]);

  const graphKey = useMemo(
    () => `${layoutKey}:${data.nodes.length}:${data.links.length}:${[...seedKeySet].sort().join(',')}`,
    [layoutKey, data.nodes.length, data.links.length, seedKeySet],
  );

  const ready = dimensions.width > 0 && dimensions.height > 0;

  const fitGraph = useCallback(() => {
    if (!graphRef.current || !data.nodes.length) {
      return;
    }
    if (data.nodes.length === 1) {
      const node = data.nodes[0];
      node.fx = 0;
      node.fy = 0;
      graphRef.current.centerAt(0, 0, 300);
      graphRef.current.zoom(1.8, 300);
      return;
    }
    graphRef.current.zoomToFit(500, 64);
  }, [data.nodes]);

  useImperativeHandle(ref, () => ({ fitGraph }), [fitGraph]);

  useEffect(() => {
    const element = hostRef.current;
    if (!element) {
      return undefined;
    }

    const measure = () => {
      const rect = element.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);
      if (width > 0 && height > 0) {
        setDimensions((prev) =>
          prev.width === width && prev.height === height ? prev : { width, height },
        );
      }
    };

    measure();
    const raf = requestAnimationFrame(measure);
    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [layoutKey, graphKey]);

  useEffect(() => {
    setHighlightNodes(null);
    setHoveredLink(null);
  }, [data]);

  useEffect(() => {
    if (!ready || !data.nodes.length) {
      return undefined;
    }
    if (multiSeed) {
      seedMultiSeedRingPositions(data.nodes, SEED_RING);
    }
    configureForces(graphRef, { multiSeed });
    return undefined;
  }, [graphKey, ready, data.nodes, multiSeed]);

  useEffect(() => {
    if (!ready || !data.nodes.length) {
      return undefined;
    }

    const delays = reducedMotion ? [50, 150] : [200, 800, 1600, 2800];
    const timers = delays.map((delay) => setTimeout(() => fitGraph(), delay));
    return () => timers.forEach(clearTimeout);
  }, [graphKey, ready, data.nodes.length, fitGraph, reducedMotion]);

  const handleEngineStop = useCallback(() => {
    if (data.nodes.length > 0) {
      fitGraph();
    }
  }, [data.nodes.length, fitGraph]);

  const paintNode = useCallback((node, ctx, globalScale) => {
    if (node.x == null || node.y == null) {
      return;
    }
    const dimmed = activeHighlight != null && !activeHighlight.has(String(node.id));
    const focused = focusHighlight != null && focusHighlight.has(String(node.id));
    const isSeed = seedKeySet.has(String(node.id)) || (node.hop ?? 0) === 0;
    const radius = nodeRadius(node, { isSeed, globalScale }) / globalScale;
    node.__hitR = radius + NODE_HIT_PAD / globalScale;
    const color = ENTITY_TYPE_COLORS[node.type] || '#9aa5b1';
    ctx.globalAlpha = dimmed ? DIM_NODE_ALPHA : 1;
    ctx.beginPath();
    ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
    ctx.fillStyle = color;
    ctx.fill();
    if (isSeed) {
      // Multi-seed: cyan ring (parity with Constellation); single-center keeps green.
      ctx.strokeStyle = multiSeed ? '#7dd3fc' : '#50ff6a';
      ctx.lineWidth = (multiSeed ? 3 : 2.8) / globalScale;
      ctx.stroke();
      drawSeedGlow(ctx, node.x, node.y, radius, globalScale, { multiSeed });
      if (multiSeed) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius + 3 / globalScale, 0, 2 * Math.PI, false);
        ctx.strokeStyle = 'rgba(125, 211, 252, 0.45)';
        ctx.lineWidth = 1.2 / globalScale;
        ctx.stroke();
      }
    }

    if (focused) {
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius + 3.5 / globalScale, 0, 2 * Math.PI, false);
      ctx.strokeStyle = FOCUS_RING_COLOR;
      ctx.lineWidth = 2 / globalScale;
      ctx.stroke();
    }

    drawNodeLabel(ctx, node, radius, globalScale, { isSeed, dimmed });
    ctx.globalAlpha = 1;
  }, [activeHighlight, focusHighlight, multiSeed, seedKeySet]);

  const paintNodePointerArea = useCallback((node, color, ctx) => {
    if (node.x == null || node.y == null) {
      return;
    }
    const r = node.__hitR || 10;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false);
    ctx.fill();
  }, []);

  const paintLink = useCallback((link, ctx, globalScale) => {
    const source = link.source;
    const target = link.target;
    if (source?.x == null || source?.y == null || target?.x == null || target?.y == null) {
      return;
    }
    const active = activeHighlight == null
      || (activeHighlight.has(endpointId(source)) && activeHighlight.has(endpointId(target)));
    const weight = Math.max(link.weight || 1, 1);
    const stroke = linkStrokeStyle(link);
    ctx.globalAlpha = active ? 1 : DIM_LINK_ALPHA;
    ctx.strokeStyle = stroke.color;
    ctx.setLineDash(stroke.dash.map((n) => n / globalScale));
    ctx.lineWidth = Math.min(0.5 + weight * 0.4, 3) / globalScale;
    ctx.beginPath();
    ctx.moveTo(source.x, source.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
    ctx.setLineDash([]);

    const midX = (source.x + target.x) / 2;
    const midY = (source.y + target.y) / 2;
    const zoomShow = globalScale > EDGE_LABEL_ZOOM && link.kind === 'RELATED' && active;
    const hoverShow = hoveredLink === link && active;
    if ((zoomShow || hoverShow) && active) {
      const label = formatLinkLabel(link, { detailed: hoverShow });
      const fontSize = Math.max(10 / globalScale, 3.5);
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const pad = 3 / globalScale;
      const metrics = ctx.measureText(label);
      const boxW = metrics.width + pad * 2;
      const boxH = fontSize + pad * 2;
      ctx.fillStyle = hoverShow ? 'rgba(12, 16, 22, 0.88)' : 'rgba(12, 16, 22, 0.72)';
      ctx.fillRect(midX - boxW / 2, midY - boxH / 2, boxW, boxH);
      ctx.fillStyle = '#e8edf2';
      ctx.fillText(label, midX, midY);
    }
    ctx.globalAlpha = 1;
  }, [activeHighlight, hoveredLink]);

  const paintLinkPointerArea = useCallback((link, color, ctx) => {
    const source = link.source;
    const target = link.target;
    if (source?.x == null || source?.y == null || target?.x == null || target?.y == null) {
      return;
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(source.x, source.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
  }, []);

  if (!data.nodes.length) {
    return (
      <div className="memetic-canvas-empty">
        {multiSeed
          ? 'No graph nodes for these seed entities in this window — try widening the time range or clearing filters.'
          : 'No graph nodes in this window — try another entity or widen the time range.'}
      </div>
    );
  }

  return (
    <div className="memetic-force-graph-host" ref={hostRef}>
      {ready && (
        <ForceGraph2D
          key={graphKey}
          ref={graphRef}
          graphData={data}
          width={dimensions.width}
          height={dimensions.height}
          backgroundColor="transparent"
          nodeRelSize={1}
          linkDirectionalParticles={0}
          cooldownTicks={reducedMotion ? 0 : 120}
          warmupTicks={reducedMotion ? 0 : 40}
          enableNodeDrag={!reducedMotion}
          enablePanInteraction
          enableZoomInteraction
          onEngineStop={handleEngineStop}
          onNodeClick={(node, event) => onNodeClick?.(node, event)}
          onNodeHover={handleNodeHover}
          onLinkHover={handleLinkHover}
          nodeCanvasObject={paintNode}
          nodeCanvasObjectMode={() => 'replace'}
          nodePointerAreaPaint={paintNodePointerArea}
          linkCanvasObject={paintLink}
          linkCanvasObjectMode={() => 'replace'}
          linkPointerAreaPaint={paintLinkPointerArea}
        />
      )}
    </div>
  );
});

MemeticGraphCanvas.displayName = 'MemeticGraphCanvas';

export { TYPE_CLASS, nodeRadius };
export default MemeticGraphCanvas;
